import axios from "axios";
import useSWRImmutable from "swr/immutable";
import { API_URL, fetcher } from "./apiConfig";
import useSWR from "swr";

const ingredientsStockFetcher = async (query, products) => {
    const response = await axios.get(query);
    const data = ingredientsInStockCheck(response.data, products);
    return data;
};

/**
 * Gets all the ingredients available at this store
 * @param storeID
 * @returns {Promise<array>} an array of ingredients
 */
export const getIngredients = (storeID) => {
    if (!storeID) storeID = 1;
    return new Promise((resolve, reject) => {
        if (pottsalatIngredients) return resolve(pottsalatIngredients);
        else return reject("no ingredients found");
    });
};

// just 'KISS' the dressings for info display
export const getDressingsFromTemplate = () => {
    if (pottsalatIngredients) {
        return pottsalatIngredients.products.filter(
            (product) =>
                product.tags.includes("ingredient.dressing") ||
                product.categories.includes("sauce")
        );
    }
    return [];
};

/**
 * useSWR hook to cache the stocks request
 * Encapsulates the ingredients API to return only values in stock
 * @returns ingredients in stock
 * @example const {dressings, extras, isLoading} = useIngredientsStock()
 */

export const useIngredientsStock = (storeId) => {
    const shouldFetch = !!pottsalatIngredients && !!storeId;
    const { products } = pottsalatIngredients;
    const productIDs = products.map((prod) => prod.product_id);
    const queryString = `${API_URL}/products/stocks?store_id=${storeId}&product_ids=${productIDs.join(
        ","
    )}`;

    const { data, error, mutate } = useSWRImmutable(
        shouldFetch ? [queryString, products] : undefined,
        ingredientsStockFetcher
    );

    return {
        dressings: data ? data.dressings : [],
        extras: data ? data.extras : [],
        bases: data ? data.bases : [],
        sauces: data ? data.sauces : [],
        isLoading: !error && !data,
        isError: !!error,
        mutateIngredientsStock: async () => await mutate(),
    };
};

const ingredientsInStockCheck = (data, ingredients) => {
    let dressings = [];
    let extras = [];
    let bases = [];
    let sauces = [];

    if (!data) {
        return null;
    }

    const stocks = data;
    ingredients.map((prod) => {
        // Find correct stock information
        const stock = stocks.find((s) => {
            return s.id === prod.product_id && s.in_stock;
        });
        // No stock was found
        if (!stock) return;
        // If product is part of extra category
        if (
            !isIngredientBasis(prod) &&
            !isIngredientDressing(prod) &&
            !isIngredientSauce(prod)
        ) {
            extras = [...extras, prod];
            return;
        }
        // if product is part of dressings catergory
        if (isIngredientDressing(prod)) {
            dressings = [...dressings, prod];
            return;
        }
        if (isIngredientSauce(prod)) {
            sauces = [...sauces, prod];
            return;
        }
        // if product is base
        if (isIngredientBasis(prod)) {
            bases = [...bases, prod];
        }
    });
    return {
        dressings,
        extras,
        bases,
        sauces,
    };
};

const isIngredientDressing = (ingredient) =>
    ingredient.categories.some((tag) => tag.includes("dressing"));

const isIngredientSauce = (ingredient) =>
    ingredient.categories.includes("sauce");

const isIngredientBasis = (ingredient) =>
    ingredient.categories.some((tag) => tag.startsWith("basis"));

/**
 * useSWR hook to cache the product stock request
 * @returns {boolean} inStock
 * @example const { inStock } = useProductStock()
 */
export const useProductStock = (storeId) => {
    const shouldFetch = !!pottsalatProduct && !!storeId;
    const { product_id: productId } = pottsalatProduct;
    const queryString = `${API_URL}/products/stocks?store_id=${storeId}&product_ids=${productId}`;

    const { data, error, mutate } = useSWRImmutable(
        shouldFetch ? queryString : undefined,
        fetcher
    );

    let inStock = false;
    if (data && data.length !== 0) {
        inStock = data[0].in_stock;
    }

    return {
        inStock,
        isLoading: !error && !data,
        mutateProductStock: async () => {
            const stock = await mutate();
            const isInStock =
                stock && stock.length !== 0 ? stock[0].in_stock : false;
            return isInStock;
        },
    };
};

export const fetchStocks = async (productIds, storeId) => {
    const queryString = `${API_URL}/products/stocks?store_id=${storeId}&product_ids=${productIds.join(
        ","
    )}`;
    try {
        const response = await axios.get(queryString);
        return { ok: true, data: response.data };
    } catch (e) {
        return { ok: false, data: [] };
    }
};

export const useStocksForProducts = (productIds, storeId) => {
    const shouldFetch = !!productIds && !!storeId;
    const queryString = `${API_URL}/products/stocks?store_id=${storeId}&product_ids=${productIds.join(
        ","
    )}`;

    const { data, error, mutate } = useSWRImmutable(
        shouldFetch ? queryString : undefined,
        fetcher
    );

    let inStock = false;
    if (data && data.length !== 0) {
        inStock = data[0].in_stock;
    }

    return {
        inStock,
        isLoading: !error && !data,
        mutateProductStock: async () => await mutate(),
    };
};

export const useComplementaryStocks = (storeId) => {
    const complementaryProducts = pottsalatProducts && [
        ...(pottsalatProducts.drinks ? pottsalatProducts.drinks : []),
        ...(pottsalatProducts.desserts ? pottsalatProducts.desserts : []),
        ...(pottsalatProducts.bread_dips ? pottsalatProducts.bread_dips : []),
        ...(pottsalatProducts.sameCategory
            ? pottsalatProducts.sameCategory
            : []),
    ];

    const complementaryProductsIds = complementaryProducts.map(
        (product) => product.product_id
    );

    const shouldFetch = complementaryProductsIds.length && storeId;

    const queryString = `${API_URL}/products/stocks?store_id=${storeId}&product_ids=${complementaryProductsIds}`;

    const { data, error } = useSWRImmutable(
        shouldFetch ? queryString : null,
        fetcher
    );

    let filteredIds = [];
    if (data) {
        const filteredData = data.filter((product) => product.in_stock);
        const dataIds = filteredData.map((product) => product.id);
        filteredIds = complementaryProductsIds.filter((id) =>
            dataIds.includes(id)
        );
    }

    return {
        inStockIds: filteredIds,
        isLoading: !error && !data,
        isError: !!error,
    };
};

export const useMenuOptions = (storeId) => {
    const { data: menuOptions, error: menuError } = useSWR(
        `${API_URL}/products?is_menu_item=true&limit=200`,
        fetcher
    );
    const { data: stocks, error: stockError } = useSWR(
        menuOptions
            ? `${API_URL}/products/stocks?store_id=${storeId}&product_ids=${menuOptions.items.map(
                  (product) => product.id
              )}`
            : null,
        fetcher
    );
    const inStockMenuOptions = menuOptions?.items.filter((menuOption) => {
        return stocks?.find(
            (stock) => stock.id === menuOption.id && stock.in_stock
        );
    });
    const groupByCategory = inStockMenuOptions?.reduce((group, product) => {
        const { category } = product;
        group[category] = group[category] ?? [];
        //TODO put into WP Config
        if (product.id === reactConfig.breadId) {
            group["default"] = [product];
        } else {
            group[category].push(product);
        }
        return group;
    }, {});

    return { menuOptions: groupByCategory, error: menuError || stockError };
};

export const useApiProduct = (productId) => {
    const { data: product, error } = useSWR(
        `${API_URL}/products/${productId}`,
        fetcher
    );
    return { product, error };
};